import { alertClasses } from "@mui/material";
import { createTheme, SimplePaletteColorOptions, Theme, Palette } from "@mui/material/styles";
import { darken, rem, rgba } from "polished";
import { CSSProperties } from "react";

import { MissionIncidentDisplayStatus } from "src/graphql-next/generated/types";
import {
  AccountQualityEnum,
  BrandCodeEnum,
  MediaPaymentStatusEnum,
  MissionStatusEnum,
  TicketStatusEnum,
} from "src/graphql/generated/types";

import { PropertyIconName } from "./property";

type PaletteExtension = {
  danger: SimplePaletteColorOptions & {
    fields: string;
  };
  gray: SimplePaletteColorOptions & {
    modalCloseButton: string;
    main: string;
    dark: string;
    contrastText?: string;
    background: string;
    tooltip: string;
    subnavTab: string;
    border: string;
    deep: string;
    credentialsCardBackground: string;
    disabled: string;
  };
  orange: SimplePaletteColorOptions & {
    boxBackground: string;
  };
  green: SimplePaletteColorOptions & {
    deep: string;
  };
  blue: SimplePaletteColorOptions & {
    focus: string;
  };
  red: SimplePaletteColorOptions;
  cyan: SimplePaletteColorOptions;
  boxShadow: string;
  textShadow: string;
  black: SimplePaletteColorOptions;
  accountSelection: {
    gray: string;
    shadow: string;
    buttonShadow: string;
    multipleAddressBackground: string;
    qualityColor: { [key in AccountQualityEnum]: string };
  };
  accountInfo: {
    background: string;
  };
  amountColor: {
    positive: string;
    negative: string;
  };
  tickets: {
    boxBackground: string;
    categoryText: string;
    border: string;
    associate: string;
    nonAssociate: string;
    contentBackground: string;
    statusText: { [key in TicketStatusEnum]: string };
  };

  dashboard: {
    unselectedGAItemBackground: string;
  };
  textLogo: {
    secondary: string;
  };
  postalVoting: {
    boxShadow: string;
  };
  datePicker: {
    border: string;
    text: string;
    placeholder: string;
  };
  table: {
    expendedComponent: string;
    filter: string;
    expendedRow: string;
    expendedRowListBorder: string;
  };
  documents: {
    background: string;
  };
  mission: {
    status: { [key in MissionStatusEnum]: string };
  };
  missionIncident: {
    status: { [key in MissionIncidentDisplayStatus]: string };
  };
  payment: {
    status: { [key in MediaPaymentStatusEnum]: string };
  };
  property: {
    icon: {
      [key in Exclude<PropertyIconName, "building">]: string;
    };
  };
};

declare module "@mui/material/styles" {
  interface Palette extends PaletteExtension {}
}

export type CustomTheme = Omit<Theme, "palette"> & {
  palette: Palette & PaletteExtension;
};

export const paletteExtension: Partial<Palette> & PaletteExtension = {
  danger: {
    light: "#ff9fa7",
    main: "#C04341",
    fields: "#CF332F",
  },

  blue: {
    light: "#F5F7FB",
    main: "#1B417B",
    focus: "#005fcc",
  },
  orange: {
    main: "#BD5113",
    dark: "#9F6D0B",
    boxBackground: "#f2e8e4",
    light: "#FCEFD3",
    contrastText: "#88683D",
  },
  gray: {
    main: "#797676",
    dark: "#576A80",
    deep: "#E5E9ED",
    background: "#f2f2f2",
    modalCloseButton: "#becadd",
    border: "#e0e0e0",
    tooltip: "#A1B8D0",
    subnavTab: "#5B7596",
    credentialsCardBackground: "#fcfcfc",
    disabled: "#000000DE",
  },
  green: {
    main: "#27833E",
    light: "#E7F8F3",
    deep: "#eff6ec99",
  },
  red: {
    light: "#ffede999",
    main: "#C04341",
  },
  cyan: {
    main: "#DFF3F7",
    light: "#F4F9FA",
    dark: "#d1e8f2",
  },
  black: {
    main: "#000000",
    light: "#0000004D",
    dark: "#000000CC",
    contrastText: "#fff",
  },
  accountSelection: {
    gray: "#7193B630",
    shadow: "0 0 28px 0 rgba(44, 64, 89, 0.42)",
    buttonShadow: "0 0 12px 0 rgba(44, 64, 89, 0.42)",
    multipleAddressBackground: "#D4DDE8",
    qualityColor: {
      [AccountQualityEnum.CoOwner]: "#2A809C",
      [AccountQualityEnum.Landlord]: "#6972B4",
      [AccountQualityEnum.Tenant]: "#21847E",
    },
  },
  accountInfo: {
    background: rgba(0, 0, 0, 0.6),
  },
  amountColor: {
    positive: "#27833E",
    negative: "#C04341",
  },
  boxShadow: "0 32px 64px rgba(0,0,0,0.07)",
  textShadow: "1px 1px 3px #fff, -1px -1px 3px #fff, -1px 1px 3px #fff, 1px -1px 3px #fff",
  background: {
    default: "#fff",
    paper: "#fff",
  },
  tickets: {
    boxBackground: "#F9FAFB",
    categoryText: "#55687E",
    border: "#adb9c7",
    associate: "#0C8487",
    nonAssociate: "#98678E",
    contentBackground: "#f9fbfe",
    statusText: {
      [TicketStatusEnum.Open]: "#B84238",
      [TicketStatusEnum.WaitingForFirstClientResponse]: "#B84238",
      [TicketStatusEnum.WorkInProgress]: "#376C8A",
      [TicketStatusEnum.Processed]: "#278040",
      [TicketStatusEnum.Finished]: "#278040",
    },
  },
  dashboard: {
    unselectedGAItemBackground: "#ececec",
  },
  textLogo: {
    secondary: "#FBFBFB",
  },
  postalVoting: {
    boxShadow: "0 2px 72px 0 rgba(0, 0, 0, 0.29)",
  },
  datePicker: {
    border: "#0000003A",
    placeholder: "#737378",
    text: "#000000de",
  },
  table: {
    expendedComponent: "#ddd",
    filter: "#dce5ee",
    expendedRow: "#eceeed",
    expendedRowListBorder: "#eee",
  },
  documents: {
    background: "#f0f4f9",
  },
  missionIncident: {
    status: {
      [MissionIncidentDisplayStatus.RequestTransmitted]: "#C85207",
      [MissionIncidentDisplayStatus.InterventionScheduled]: "#A76906",
      [MissionIncidentDisplayStatus.Completed]: "#27833E",
    },
  },
  mission: {
    status: {
      [MissionStatusEnum.Open]: "#C85207",
      [MissionStatusEnum.WorkInProgress]: "#A76906",
      [MissionStatusEnum.Summoned]: "#A76906",
      [MissionStatusEnum.MinutesSent]: "#A76906",
      [MissionStatusEnum.Finished]: "#27833E",
      [MissionStatusEnum.Close]: "#C04341",
      [MissionStatusEnum.Ended]: "#C04341",
    },
  },
  payment: {
    status: {
      [MediaPaymentStatusEnum.Archived]: "#C04341",
      [MediaPaymentStatusEnum.Awaiting]: "#FFBA74",
      [MediaPaymentStatusEnum.Cancelled]: "#C04341",
      [MediaPaymentStatusEnum.Consolidated]: "#436CF2",
      [MediaPaymentStatusEnum.PendingBuildingTransfer]: "#436CF2",
      [MediaPaymentStatusEnum.Processed]: "#27833E",
      [MediaPaymentStatusEnum.Quarantine]: "#C04341",
      [MediaPaymentStatusEnum.Rejected]: "#C04341",
      [MediaPaymentStatusEnum.Standby]: "#FFBA74",
      [MediaPaymentStatusEnum.ToProcess]: "#436CF2",
      [MediaPaymentStatusEnum.Suspended]: "#C04341",
      [MediaPaymentStatusEnum.ErrorProcess]: "#C04341",
      [MediaPaymentStatusEnum.Processing]: "#436CF2",
      [MediaPaymentStatusEnum.Transfered]: "#27833E",
    },
  },
  property: {
    icon: {
      box: "#f24ca5",
      cellar: "#44d7b6",
      parking: "#eb690b",
      home: "#4a90e2",
    },
  },
};

// TODO: accessibility colors for foncia
// primary: {
//   light: "#F2FAFB",
//   main: "#2A809C",
//   dark: "#2A809C",
//   contrastText: "#fff",
// },
// secondary: {
//   light: "#fff",
//   main: "#2A809C",
//   dark: "#26535E",
//   contrastText: "#fff",
// }

// eslint-disable-next-line no-restricted-syntax
const fonciaPaletteExtension: Partial<Palette> & PaletteExtension = {
  ...paletteExtension,
  primary: {
    light: "#F5FCFD",
    main: "#387691",
    dark: "#387691",
    contrastText: "#fff",
  },
  secondary: {
    light: "#fff",
    main: "#387691",
    dark: "#2B6573",
    contrastText: "#fff",
  },
};

const essetPaletteExtension: Partial<Palette> & PaletteExtension = {
  ...paletteExtension,
  primary: {
    light: "#F5FBF9",
    main: "#098359",
    dark: "#098359",
    contrastText: "#fff",
  },
  secondary: {
    light: "#fff",
    main: "#098359",
    dark: darken(0.2, "#098359"),
    contrastText: "#fff",
  },
};

export const focusOutline: CSSProperties = {
  outline: `2px solid ${paletteExtension.blue.focus} !important`,
  outlineOffset: "2px",
  boxShadow: `0 0 0 1px ${paletteExtension.black.contrastText} !important`,
};

const materialBaseTheme = createTheme({
  typography: {
    fontFamily: "Montserrat, Roboto, Helvetica, Arial, sans-serif",
    body1: {
      fontSize: 14,
    },
    h1: {
      fontSize: rem(34),
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ".MuiBox-root:focus-visible": focusOutline,
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: "none",
        },
      },
    },
    MuiButton: {
      defaultProps: {
        type: "button",
        disableRipple: true,
        color: "secondary",
      },
      styleOverrides: {
        root: {
          textTransform: "none",
          "&.Mui-focusVisible": focusOutline,
          "& .Mui-disabled": {
            color: paletteExtension.gray.disabled,
            backgroundColor: paletteExtension.background?.default,
          },
        },
        contained: {
          boxShadow: "none",
          "&:hover": {
            boxShadow: "none",
          },
        },
      },
    },
    MuiSwitch: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        switchBase: {
          "&.Mui-focusVisible > span": focusOutline,
          "&.Mui-focusVisible": {
            outline: "none !important",
            boxShadow: "none !important",
          },
        },
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          fontFamily: "inherit",
          "&.Mui-focusVisible": focusOutline,
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          "&.Mui-focused": focusOutline,
        },
      },
    },
    MuiIconButton: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.04)",
          },
          "&.Mui-focusVisible": focusOutline,

          "&:focus-visible": focusOutline,
        },
      },
    },
    MuiCheckbox: {
      defaultProps: {
        color: "secondary",
        disableRipple: true,
        tabIndex: 0,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.secondary.dark,
          "&.Mui-checked": {
            color: theme.palette.secondary.dark,
          },
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.04)",
          },
          "&.Mui-focusVisible": focusOutline,
        }),
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          "&.Mui-focusVisible": {
            ...focusOutline,
            backgroundColor: "unset",
          },
          "&:hover": {
            backgroundColor: `${theme.palette.primary.main} !important`,
            outline: `2px ${theme.palette.primary.main} !important`,
          },
        }),
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          [`&.${alertClasses.standard}.${alertClasses.colorWarning}`]: {
            backgroundColor: paletteExtension.orange.light,
            color: paletteExtension.orange.contrastText,
            "& .MuiAlert-icon": {
              color: paletteExtension.orange.contrastText,
            },
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          "& .Mui-disabled": {
            color: `${paletteExtension.gray.disabled} !important`,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          "& .Mui-disabled": {
            color: `${paletteExtension.gray.disabled} !important`,
          },
        },
      },
    },
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          "&.Mui-focusVisible": {
            ...focusOutline,
            backgroundColor: "unset",
          },
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          "&.Mui-disabled": {
            color: paletteExtension.gray.disabled,
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .Mui-error": {
            color: paletteExtension.danger.fields,
          },
          "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
            borderColor: paletteExtension.danger.fields,
          },
          "& .Mui-Disabled": {
            "-webkit-text-fill-color": `${paletteExtension.gray.disabled}  !important`,
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          "&.Mui-error": {
            color: paletteExtension.danger.fields,
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          "&.Mui-error": {
            color: paletteExtension.danger.fields,
          },
        },
      },
    },
  },
  spacing: (factor: number) => rem(8 * factor),
});

export const brandPalettes: Record<BrandCodeEnum, Partial<Palette> & PaletteExtension> = {
  [BrandCodeEnum.Foncia]: fonciaPaletteExtension,
  [BrandCodeEnum.Esset]: essetPaletteExtension,
};

export const getThemeForBrand = ({ brandCode }: { brandCode: BrandCodeEnum }): CustomTheme => {
  const customPalette = brandPalettes[brandCode] ?? {};

  return {
    ...materialBaseTheme,
    palette: {
      ...materialBaseTheme.palette,
      ...customPalette,
    },
  } as CustomTheme;
};
